import styled from 'styled-components';

import { NOTIFICATION_TYPES } from '@savgroup-front-common/constants';
import { lighten } from '@savgroup-front-common/primitive/helpers/cssColor';
import { fontWeight } from '@savgroup-front-common/primitive/helpers/fontWeight';
import { spacing } from '@savgroup-front-common/primitive/helpers/spacing';
import { rem } from '@savgroup-front-common/primitive/helpers/units';
import {
  DefaultStyledProps,
  SUPPORTED_COMPONENTS,
  SUPPORTED_PROPERTIES,
} from '@savgroup-front-common/types';

import { styledGetCustomComponentProperty } from '../../helpers/theme';
import { fontStyle } from '../../theme/helpers/fontStyle';

interface NotificationTheming extends DefaultStyledProps {
  hollow?: boolean;
  notificationType?: NOTIFICATION_TYPES;
}

export const iconColorBasedOnProps = ({
  theme,
  notificationType,
}: NotificationTheming): string => {
  if (notificationType === NOTIFICATION_TYPES.INFO) {
    return theme.newUI.tagsColors.primary.color;
  }
  if (notificationType === NOTIFICATION_TYPES.SUCCESS) {
    return theme.newUI.tagsColors.success.color;
  }
  if (notificationType === NOTIFICATION_TYPES.ALERT) {
    return theme.newUI.tagsColors.alert.color;
  }
  if (notificationType === NOTIFICATION_TYPES.ERROR) {
    return theme.newUI.tagsColors.danger.color;
  }

  return theme.newUI.iconColors.primary.color;
};

export const colorBasedOnProps = ({
  theme,
  notificationType,
}: NotificationTheming): string => {
  if (notificationType === NOTIFICATION_TYPES.INFO) {
    return theme.newUI.tagsColors.primary.bgColor;
  }
  if (notificationType === NOTIFICATION_TYPES.SUCCESS) {
    return theme.newUI.tagsColors.success.bgColor;
  }
  if (notificationType === NOTIFICATION_TYPES.ALERT) {
    return theme.newUI.tagsColors.alert.bgColor;
  }
  if (notificationType === NOTIFICATION_TYPES.ERROR) {
    return theme.newUI.tagsColors.danger.bgColor;
  }
  if (notificationType === NOTIFICATION_TYPES.DEFAULT) {
    return theme.colors.white;
  }

  return theme.colors.primary;
};

interface NotificationWrapperProps extends NotificationTheming {
  $isFluid: boolean;
  notificationType?: NOTIFICATION_TYPES;
  $withMarginBottom?: boolean;
  $isBold?: boolean;
}

export const $NotificationWrapper = styled.div<NotificationWrapperProps>`
  height: auto;
  overflow: hidden;

  ${fontStyle('sm')};

  color: ${({ theme }) => theme.systemColors.defaultTextColor};
  svg {
    color: ${({ theme, notificationType }) =>
      iconColorBasedOnProps({ theme, notificationType })};
  }

  font-weight: ${({ $isBold }) =>
    $isBold ? fontWeight('bold') : fontWeight('medium')};

  background-color: ${(props) => {
    if (props.hollow) {
      return props.theme.newUI.tagsColors.alert.bgColor;
    }

    return colorBasedOnProps(props);
  }};

  border-radius: ${({ theme }) => theme.newUI.borders.bigRadius};
  border: ${({ theme, hollow, notificationType }) =>
    (!hollow && notificationType === NOTIFICATION_TYPES.INFO) ||
    notificationType === NOTIFICATION_TYPES.DEFAULT
      ? `1px solid ${theme.newUI.variationColors?.light4}`
      : null};

  max-width: ${({ $isFluid }) => ($isFluid ? null : spacing(138))};

  margin: 0 auto;
  margin-bottom: ${({ $withMarginBottom }) =>
    $withMarginBottom ? spacing(3.5) : null};

  display: flex;
  align-items: center;
  min-height: ${styledGetCustomComponentProperty({
    componentName: SUPPORTED_COMPONENTS.DEFAULT_INPUTS,
    property: SUPPORTED_PROPERTIES.HEIGHT,
  })};
`;

export const $IconContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  min-width: ${spacing(12)};
`;

interface TextContainerProps {
  $hasIcon: boolean;
}

const textPadding = 7;

export const $TextContainer = styled.div<TextContainerProps>`
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
  padding: ${({ $hasIcon }) =>
    $hasIcon ? `${rem(textPadding)} 0 ${rem(textPadding)}` : rem(textPadding)};
  &:last-child {
    padding-right: ${spacing('lg')};
  }
`;

interface NotificationContainerProps {
  $height: number;
}

export const $NotificationContainer = styled.div<NotificationContainerProps>`
  &:last-child {
    padding-bottom: 0;
  }
`;

type CloseContainerProps = NotificationTheming;

export const $CloseContainer = styled.button<CloseContainerProps>`
  min-width: ${spacing('xl')};
  background: none;
  outline: none;
  border: 0;
  cursor: pointer;
  margin-right: ${spacing(2.5)};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition-duration: 200ms;

  svg {
    color: ${({ theme, notificationType }) =>
      iconColorBasedOnProps({ theme, notificationType })};
  }

  &:hover,
  &:focus {
    background-color: ${(props) =>
      props.hollow
        ? colorBasedOnProps(props)
        : lighten(0.15, colorBasedOnProps(props))};
  }
`;
